import { css } from 'styled-components';
import Colr from 'colr';
import { buttonReset, getSmallButtonTokens, getMediumButtonTokens, getLargeButtonTokens, getPrimaryButtonTokens, getPrimaryButtonSelectedTokens, getPrimaryButtonDisabledTokens, getSecondaryButtonTokens, getSecondaryButtonSelectedTokens, getSecondaryButtonDisabledTokens, getTertiaryButtonTokens, getTertiaryButtonSelectedTokens, getTertiaryButtonDisabledTokens, getDestructiveButtonTokens, getDestructiveButtonSelectedTokens, getDestructiveButtonDisabledTokens } from '../shared';
import common, { spacing } from '../../common';
import token from '../../getToken';
export const hoverColor = color => Colr.fromHex(color).darken(10).toHex();
export const activeColor = color => Colr.fromHex(color).darken(20).toHex();
export const disabledColor = (color, percent) => {
  const opacity = percent || 0.3;
  const rgb = Colr.fromHex(color).toRgbArray().toString();
  return `rgba(${rgb}, ${opacity})`;
};
const typeScale = {
  1: '4rem',
  2: '3rem',
  3: '2rem',
  4: '1.5rem',
  5: '1rem',
  6: '.875rem'
};
const lineHeight = {
  tighter: 1,
  tight: 1.2,
  normal: 1.5
};
const tracking = {
  normal: '-0.01em'
};
const fonts = {
  heading: '"Fresh Sans", "Arial", "Helvetica", sans-serif',
  copy: '"Roboto", "Arial", "Helvetica", sans-serif'
};
const ediColors = {
  everydayOrange: '#fd6400',
  darkOrange: '#d44500',
  charcoal: '#3a474e',
  lightPistachio: '#e1ebc8',
  pistachio: '#9cd081',
  lightAqua: '#cce9dc',
  aqua: '#65c6b7',
  forestGreen: '#0d3a2b'
};
const colors = {
  brand: {
    base: ediColors.everydayOrange,
    light: ediColors.lightPistachio,
    dark: ediColors.darkOrange
  },
  accent: ediColors.pistachio,
  shades: {
    black: '#000000',
    darkest: '#222c2b',
    darker: '#49545a',
    dark: '#9b9b9d',
    light: '#cacaca',
    lighter: '#e2e2e2',
    lightest: '#f2f2f2',
    nearWhite: '#f8f8f8',
    white: '#ffffff'
  },
  focus: '#005fcc',
  error: '#a94442',
  success: ediColors.forestGreen,
  info: ediColors.aqua,
  product: {
    hospital: ediColors.everydayOrange,
    extras: ediColors.pistachio,
    combined: ediColors.aqua,
    highlight: {
      backgroundColor: ediColors.darkOrange,
      textColor: '#ffffff'
    }
  }
};
const focus = {
  outlineColor: colors.focus,
  outlineStyle: 'solid',
  outlineWidth: '2px',
  outlineOffset: '0.125em'
};
const baseButtonStyles = css`
  ${buttonReset};
  font-family: ${fonts.heading};
  font-weight: 500;
  line-height: ${lineHeight.tighter};
  letter-spacing: ${tracking.normal};
  border-radius: ${token('button.dimension.border.radius', '8px')};
  border-width: ${token('button.dimension.border.width', '1px')};

  &:disabled {
    cursor: default;
    pointer-events: auto;
    color: ${colors.shades.darkest};
    background-color: ${colors.shades.lightest};
  }
`;
const buttonStyles = {
  primary: {
    normal: css`
      ${baseButtonStyles};
      ${getPrimaryButtonTokens({
      bg: {
        default: colors.brand.dark,
        hover: hoverColor(colors.brand.dark),
        active: activeColor(colors.brand.dark),
        disabled: disabledColor(colors.shades.light, 0.3)
      },
      fg: {
        default: colors.shades.white,
        hover: colors.shades.white,
        active: colors.shades.light,
        disabled: disabledColor(colors.shades.darkest, 0.3)
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent'
      }
    })};
    `,
    selected: css`
      ${getPrimaryButtonSelectedTokens({
      bg: activeColor(colors.brand.dark),
      fg: colors.shades.light,
      border: 'transparent'
    })};
    `,
    disabled: css`
      ${getPrimaryButtonDisabledTokens({
      bg: disabledColor(colors.shades.light, 0.3),
      fg: disabledColor(colors.shades.darkest, 0.3),
      border: 'transparent'
    })};
      cursor: not-allowed;
    `
  },
  secondary: {
    normal: css`
      ${baseButtonStyles};
      ${getSecondaryButtonTokens({
      bg: {
        default: colors.shades.white,
        hover: colors.shades.lightest,
        active: colors.shades.lighter,
        disabled: disabledColor(colors.shades.light, 0.3)
      },
      fg: {
        default: ediColors.charcoal,
        hover: hoverColor(ediColors.charcoal),
        active: activeColor(ediColors.charcoal),
        disabled: disabledColor(colors.shades.darkest, 0.3)
      },
      border: {
        default: ediColors.charcoal,
        hover: hoverColor(ediColors.charcoal),
        active: activeColor(ediColors.charcoal),
        disabled: disabledColor(colors.shades.darkest, 0.3)
      }
    })};
    `,
    selected: css`
      ${getSecondaryButtonSelectedTokens({
      bg: colors.shades.lighter,
      fg: activeColor(ediColors.charcoal),
      border: activeColor(ediColors.charcoal)
    })};
    `,
    disabled: css`
      ${getSecondaryButtonDisabledTokens({
      bg: disabledColor(colors.shades.light, 0.3),
      fg: disabledColor(ediColors.charcoal, 0.3),
      border: disabledColor(ediColors.charcoal, 0.3)
    })};
      cursor: not-allowed;
    `
  },
  tertiary: {
    normal: css`
      ${baseButtonStyles};
      ${getTertiaryButtonTokens({
      bg: {
        default: colors.shades.white,
        hover: colors.shades.lightest,
        active: colors.shades.lighter,
        disabled: disabledColor(colors.shades.light, 0.3)
      },
      fg: {
        default: colors.shades.darkest,
        hover: hoverColor(colors.shades.darkest),
        active: activeColor(colors.shades.darkest),
        disabled: disabledColor(colors.shades.darkest, 0.3)
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent'
      }
    })};
    `,
    selected: css`
      ${getTertiaryButtonSelectedTokens({
      bg: colors.shades.lighter,
      fg: activeColor(colors.shades.darkest),
      border: 'transparent'
    })};
    `,
    disabled: css`
      ${getTertiaryButtonDisabledTokens({
      bg: disabledColor(colors.shades.light, 0.3),
      fg: disabledColor(colors.shades.darkest, 0.3),
      border: 'transparent'
    })};
      cursor: not-allowed;
    `
  },
  destructive: {
    normal: css`
      ${baseButtonStyles};
      ${getDestructiveButtonTokens({
      bg: {
        default: colors.shades.white,
        hover: colors.shades.lightest,
        active: colors.shades.lighter,
        disabled: disabledColor(colors.shades.light, 0.3)
      },
      fg: {
        default: colors.error,
        hover: hoverColor(colors.error),
        active: activeColor(colors.error),
        disabled: disabledColor(colors.error, 0.4)
      },
      border: {
        default: colors.error,
        hover: hoverColor(colors.error),
        active: activeColor(colors.error),
        disabled: disabledColor(colors.error, 0.4)
      }
    })};
    `,
    selected: css`
      ${getDestructiveButtonSelectedTokens({
      bg: colors.shades.lighter,
      fg: activeColor(colors.error),
      border: activeColor(colors.error)
    })};
    `,
    disabled: css`
      ${getDestructiveButtonDisabledTokens({
      bg: disabledColor(colors.shades.light, 0.3),
      fg: disabledColor(colors.error, 0.4),
      border: disabledColor(colors.error, 0.4)
    })};
      cursor: not-allowed;
    `
  },
  small: css`
    ${getSmallButtonTokens({
    fontFamily: fonts.heading,
    fontSize: typeScale[6],
    fontWeight: 500,
    lineHeight: lineHeight.tighter,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[3],
    paddingInline: spacing[4],
    gap: spacing[2]
  })};
  `,
  medium: css`
    ${getMediumButtonTokens({
    fontFamily: fonts.heading,
    fontSize: typeScale[6],
    fontWeight: 500,
    lineHeight: lineHeight.tighter,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[4],
    paddingInline: '1.25rem',
    gap: spacing[2]
  })};
  `,
  large: css`
    ${getLargeButtonTokens({
    fontFamily: fonts.heading,
    fontSize: typeScale[5],
    fontWeight: 500,
    lineHeight: lineHeight.tighter,
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: '1.125rem',
    paddingInline: spacing[5],
    gap: spacing[2]
  })};
  `
};
const baseFormValues = {
  borderColor: colors.shades.light,
  borderColorValid: colors.success,
  borderColorInvalid: colors.error,
  selectedToggleColor: colors.brand.dark,
  helpTextColor: colors.shades.dark,
  helpTextSize: typeScale[6],
  labelColor: ediColors.charcoal,
  labelSize: typeScale[5],
  errorMessageTextColor: colors.error,
  infoMessageTextColor: colors.brand.dark
};
const edi = {
  ...common,
  borderRadius: {
    ...common.borderRadius,
    small: 'var(--themeDimensionBorderRadiusSmall, 3px)',
    standard: 'var(--themeDimensionBorderRadiusStandard,15px)'
  },
  typeScale,
  fonts,
  colors,
  focus,
  id: 'edi',
  mode: {
    form: 'light' //default
  },
  headings: {
    fontFamily: fonts.heading,
    color: 'currentColor',
    lineHeight: lineHeight.tighter,
    h1: {
      fontFamily: fonts.heading,
      fontSize: typeScale[1],
      fontWeight: 500,
      lineHeight: lineHeight.tighter,
      tracking: tracking.normal,
      uppercase: false
    },
    h2: {
      fontFamily: fonts.heading,
      fontSize: typeScale[2],
      lineHeight: lineHeight.tighter,
      fontWeight: 500,
      tracking: tracking.normal,
      uppercase: false
    },
    h3: {
      fontFamily: fonts.heading,
      fontSize: typeScale[3],
      lineHeight: lineHeight.tighter,
      fontWeight: 500,
      tracking: tracking.normal,
      uppercase: false
    },
    h4: {
      fontFamily: fonts.heading,
      fontSize: typeScale[4],
      lineHeight: lineHeight.tighter,
      fontWeight: 500,
      tracking: tracking.normal,
      uppercase: false
    },
    h5: {
      fontFamily: fonts.heading,
      fontSize: typeScale[5],
      lineHeight: lineHeight.tight,
      fontWeight: 500,
      tracking: tracking.normal,
      uppercase: false
    },
    h6: {
      fontFamily: fonts.heading,
      fontSize: typeScale[6],
      lineHeight: lineHeight.tight,
      fontWeight: 500,
      tracking: tracking.normal,
      uppercase: false
    }
  },
  copy: {
    color: ediColors.charcoal,
    fontFamily: fonts.copy,
    fontSize: typeScale[5],
    fontWeight: 400,
    lineHeight: lineHeight.normal,
    smallFontSize: typeScale[6],
    largeFontSize: typeScale[4]
  },
  link: {
    loudColor: colors.brand.dark,
    darkColor: colors.shades.darkest,
    lightColor: colors.shades.white,
    destructiveColor: colors.error
  },
  button: {
    small: buttonStyles.small,
    medium: buttonStyles.medium,
    large: buttonStyles.large,
    primary: buttonStyles.primary,
    secondary: buttonStyles.secondary,
    tertiary: buttonStyles.tertiary,
    destructive: buttonStyles.destructive
  },
  forms: {
    light: {
      ...baseFormValues,
      backgroundColor: colors.shades.lightest,
      inputBackgroundColor: colors.shades.white,
      boxShadow: `none`
    },
    white: {
      ...baseFormValues,
      backgroundColor: colors.shades.white,
      inputBackgroundColor: colors.shades.lightest,
      boxShadow: 'none'
    }
  },
  smallFooter: {
    backgroundColor: colors.shades.lighter,
    textColor: colors.shades.darkest,
    linkColor: colors.shades.darkest
  },
  panel: {
    loud: {
      backgroundColor: colors.brand.base,
      textColor: colors.shades.darkest
    },
    muted: {
      backgroundColor: colors.shades.lightest,
      textColor: colors.shades.darkest
    },
    light: {
      backgroundColor: colors.shades.lightest,
      textColor: colors.shades.darkest
    },
    dark: {
      backgroundColor: colors.shades.darkest,
      textColor: colors.shades.white
    },
    white: {
      backgroundColor: colors.shades.white,
      textColor: colors.shades.darkest
    }
  }
};
export default edi;